import React, { useEffect, useState } from 'react';
import { coachCheckinAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  UserCheck,
  Shield,
  Clock,
  CheckCircle,
  Users,
  Key,
  Lock,
  Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const FOOTBALL_POSITIONS = [
  { value: 'QB', label: 'Quarterbacks' },
  { value: 'RB', label: 'Running Backs' },
  { value: 'WR', label: 'Wide Receivers' },
  { value: 'TE', label: 'Tight Ends' },
  { value: 'OL', label: 'Offensive Line' },
  { value: 'DB', label: 'Defensive Backs' },
  { value: 'LB', label: 'Linebackers' },
  { value: 'DL', label: 'Defensive Line' },
];

const DAY_TYPES = {
  green: { label: 'Green Day', color: 'bg-[#32D74B]', textColor: 'text-[#32D74B]', description: 'Fundamentals, Habits' },
  black: { label: 'Black Day', color: 'bg-[#27272A]', textColor: 'text-white', description: 'PHYSICAL / ATTITUDE' },
  orange: { label: 'Orange Day', color: 'bg-[#FF9500]', textColor: 'text-[#FF9500]', description: 'Recovery focused' },
};

export default function CoachCheckinPage() {
  const { user, canApproveUsers } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [todayCheckins, setTodayCheckins] = useState([]);
  const [checkinsByPosition, setCheckinsByPosition] = useState({});
  const [todayPassword, setTodayPassword] = useState(null);
  
  // Check-in form
  const [password, setPassword] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  
  // Set password modal (for admins)
  const [setPasswordModal, setSetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, todayRes, byPosRes] = await Promise.all([
        coachCheckinAPI.getMyStatus(),
        coachCheckinAPI.getTodayCheckins(),
        coachCheckinAPI.getByPosition()
      ]);
      
      setCheckinStatus(statusRes.data);
      setTodayCheckins(todayRes.data.checkins || []);
      setCheckinsByPosition(byPosRes.data);
      
      // If admin, also get current password
      if (canApproveUsers) {
        try {
          const pwRes = await coachCheckinAPI.getPassword();
          setTodayPassword(pwRes.data);
        } catch (e) {
          // Password might not be set
        }
      }
    } catch (error) {
      toast.error('Failed to load check-in data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    if (!password) {
      toast.error('Please enter the check-in password');
      return;
    }
    if (!selectedPosition) {
      toast.error('Please select your position group');
      return;
    }
    
    setCheckingIn(true);
    try {
      await coachCheckinAPI.checkin(password, selectedPosition);
      toast.success('Check-in successful!');
      setPassword('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword) {
      toast.error('Please enter a password');
      return;
    }
    
    try {
      const res = await coachCheckinAPI.setPassword(newPassword);
      toast.success(`Password set: ${res.data.password}`);
      setSetPasswordModal(false);
      setNewPassword('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    }
  };

  const dayTypeInfo = DAY_TYPES[checkinStatus?.day_type || 'black'];
  const today = format(new Date(), 'EEEE, MMMM d, yyyy');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B30]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#FF3B30] mb-4">
            <UserCheck className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-display text-5xl text-white tracking-tight">COACH CHECK-IN</h1>
          <p className="text-[#71717A] mt-2 text-lg">{today}</p>
          
          {/* Day Type Indicator */}
          <div className={`inline-flex items-center gap-3 px-6 py-3 mt-4 ${dayTypeInfo.color}`}>
            <span className="text-white font-bold text-xl">{dayTypeInfo.label}</span>
            <span className="text-white/80">- {dayTypeInfo.description}</span>
          </div>
        </div>

        {/* Admin Controls */}
        {canApproveUsers && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-[#FFD60A]" />
                Password Management (Admin)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A1A1AA]">Today's Password:</p>
                  {todayPassword?.password ? (
                    <p className="text-2xl font-mono text-white font-bold tracking-widest">
                      {todayPassword.password}
                    </p>
                  ) : (
                    <p className="text-[#FF9500]">No password set for today</p>
                  )}
                </div>
                <Button
                  onClick={() => setSetPasswordModal(true)}
                  className="bg-[#FFD60A] hover:bg-[#FFD60A]/90 text-black"
                  data-testid="set-password-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {todayPassword?.password ? 'Change Password' : 'Set Password'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Check-in Form */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="text-white flex items-center gap-2">
                {checkinStatus?.checked_in ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-[#32D74B]" />
                    You're Checked In
                  </>
                ) : (
                  <>
                    <Clock className="w-5 h-5 text-[#FF9500]" />
                    Check In Now
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {checkinStatus?.checked_in ? (
                <div className="text-center py-8">
                  <div className="w-24 h-24 bg-[#32D74B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-12 h-12 text-[#32D74B]" />
                  </div>
                  <p className="text-white text-xl font-medium">{user?.name}</p>
                  <p className="text-[#71717A] mt-1">
                    Checked in at {format(new Date(checkinStatus.checkin.checked_in_at), 'h:mm a')}
                  </p>
                  {checkinStatus.checkin.position && (
                    <p className="text-[#FF3B30] mt-2 text-lg">
                      {FOOTBALL_POSITIONS.find(p => p.value === checkinStatus.checkin.position)?.label || checkinStatus.checkin.position}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <Label className="text-[#A1A1AA]">Check-in Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                      <Input
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value.toUpperCase())}
                        placeholder="Enter password"
                        className="bg-[#0A0A0A] border-[#27272A] text-white text-center text-2xl font-mono tracking-widest uppercase pl-12 h-14"
                        maxLength={8}
                        data-testid="checkin-password-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#A1A1AA]">Your Position Group</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {FOOTBALL_POSITIONS.map((pos) => (
                        <button
                          key={pos.value}
                          type="button"
                          onClick={() => setSelectedPosition(pos.value)}
                          className={`p-3 border text-left transition-all ${
                            selectedPosition === pos.value 
                              ? 'border-[#FF3B30] bg-[#FF3B30]/10' 
                              : 'border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]'
                          }`}
                          data-testid={`position-${pos.value}`}
                        >
                          <span className="font-bold text-white">{pos.value}</span>
                          <span className="text-[#71717A] text-sm ml-2">{pos.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckin}
                    disabled={checkingIn}
                    className="w-full h-14 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white text-lg font-bold"
                    data-testid="checkin-submit-btn"
                  >
                    <Unlock className="w-5 h-5 mr-2" />
                    {checkingIn ? 'Checking in...' : 'CHECK IN'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Today's Check-ins */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5" />
                Coaches Here Today ({todayCheckins.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {todayCheckins.length > 0 ? (
                <div className="divide-y divide-[#27272A]">
                  {todayCheckins.map((checkin) => {
                    const posInfo = FOOTBALL_POSITIONS.find(p => p.value === checkin.position);
                    
                    return (
                      <div key={checkin.id} className="flex items-center gap-4 p-4 hover:bg-[#1A1A1A]">
                        <div className="w-12 h-12 bg-[#32D74B]/10 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-[#32D74B]" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{checkin.user_name}</p>
                          <p className="text-sm text-[#71717A]">
                            {checkin.user_role.replace('_', ' ').toUpperCase()}
                          </p>
                        </div>
                        <div className="text-right">
                          {posInfo && (
                            <p className="text-[#FF3B30] font-bold">{posInfo.value}</p>
                          )}
                          <p className="text-xs text-[#71717A]">
                            {format(new Date(checkin.checked_in_at), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-[#71717A]">
                  <Users className="w-12 h-12 mb-3 opacity-50" />
                  <p>No coaches checked in yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Check-ins by Position */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="border-b border-[#27272A]">
            <CardTitle className="text-white">Coaches by Position</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {FOOTBALL_POSITIONS.map((pos) => {
                const coaches = checkinsByPosition[pos.value] || [];
                
                return (
                  <div key={pos.value} className="bg-[#0A0A0A] border border-[#27272A] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#FF3B30] font-bold text-lg">{pos.value}</span>
                      <span className="text-[#71717A] text-sm">{coaches.length}</span>
                    </div>
                    <p className="text-xs text-[#71717A] mb-2">{pos.label}</p>
                    {coaches.length > 0 ? (
                      <div className="space-y-1">
                        {coaches.map((c) => (
                          <p key={c.id} className="text-sm text-white truncate">
                            {c.user_name}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#52525B]">No coaches</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Back to Dashboard */}
        <div className="text-center">
          <a 
            href="/dashboard" 
            className="text-[#71717A] hover:text-white transition-colors"
          >
            Back to Dashboard
          </a>
        </div>
      </div>

      {/* Set Password Modal */}
      <Dialog open={setPasswordModal} onOpenChange={setSetPasswordModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#FFD60A]" />
              Set Daily Check-in Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-[#A1A1AA] text-sm">
              This password will be required for all coaches to check in today. 
              Share it at the start of the day.
            </p>
            <div>
              <Label className="text-[#A1A1AA]">Password</Label>
              <Input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value.toUpperCase())}
                placeholder="e.g., TEAM2026"
                className="bg-[#0A0A0A] border-[#27272A] text-white text-center text-xl font-mono tracking-widest uppercase mt-1"
                maxLength={10}
                data-testid="new-password-input"
              />
              <p className="text-xs text-[#71717A] mt-1">
                Password will be converted to uppercase
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSetPasswordModal(false)}
              className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              className="bg-[#FFD60A] hover:bg-[#FFD60A]/90 text-black"
              data-testid="confirm-password-btn"
            >
              Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
